 import React, {memo, useCallback, useState, useEffect, useMemo} from "react"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {BusyIndicator} from "../../../../components/BusyIndicator"
import {useLocalization} from "../../../../hooks/localization"
import {onClickHandler} from "../../../../utils/onclick"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useMessage} from "../../../../hooks/message"
import {useRPC} from "../../../../hooks/rpc"
import {useDispatch, useSelector} from "react-redux"
import { ApplicationState } from "../../../../store/reducer"
import { setCloudConnectionStatusAction, setCloudStatus } from "../../../../store/control/actions"
import {setPopupScreenAction} from "../../../../store/ui/actions"
import {MappingLogPopupScreen} from "../../Gateway/Setup/SetupMatchScreen"
import "./index.scss"

export const CloudActionsEditScreen = memo(function CloudActionsEditScreen() {
    const {t} = useLocalization()
    const {success, failure} = useMessage()
    const [rpc] = useRPC()
    const dispatch = useDispatch()
    const [setActiveInput, renderKeyboard] = useKeyboard()
    const [cloudConfig, setCloudConfig] = useState(null)
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [loadingType, setLoadingType] = useState<'login' | 'logout' | 'serviceUpdate' | null>(null)
    const [connectServices, setConnectServices] = useState(null)
    const [openMappingLog, setOpenMappingLog] = useState(false)
    const [classNames, setClassNames] = useState(['mapping-log-container'])
    const cloudEnabled = useSelector((state: ApplicationState) => state.control.cloudEnabled)
    const mappingLogs = useSelector(state => state.control.cloudEvent)
    const [mappingLogsResult, setMappingLogsResult] = useState<string[]>([])
    
    const gatewayList = useSelector(state => state.control.gatewayList);

    const selectedGateway = useMemo(() => gatewayList.find(gateway => gateway.id) || null, [gatewayList]);

    useEffect(() => {
        const fetchCloudConfig = async () => {
            try {
                const config = await rpc.getCloudConfig()
                setCloudConfig(config)
                setEmail(config?.email || "")
            } catch (error) {
                failure(error)
                rpc.frontendErrorLog("Cloud config error:" + error)
            }
        }
        
        fetchCloudConfig()
    }, [rpc, failure])

    useEffect(() => {
        rpc.getCloudStatus()
            .then((enabled) => {
                if (enabled === true || enabled === false) {
                    dispatch(setCloudStatus(enabled))
                }
            })
            .catch(() => {})
    }, [rpc, dispatch])

    useEffect(() => {
        const fetchConnectionServices = async () => {
            try {
                const connect = await rpc.getConnectionServices()
                setConnectServices(connect)
            } catch (error) {
                failure(error)
            }
        }
        
        fetchConnectionServices()
    }, [rpc, failure])

    useEffect(() => {
        if (openMappingLog && mappingLogs && mappingLogs !== "null") {
            setMappingLogsResult(prev => [...prev, mappingLogs])
        }
    }, [mappingLogs, openMappingLog])

    useEffect(() => {
        if (!openMappingLog) return;
        const lastLog = mappingLogsResult[mappingLogsResult.length - 1];
        
        let newClassName = 'mapping-log-container';
        
        if (lastLog && typeof lastLog === 'string') {
            if (lastLog.includes('cloud_test_finished') || lastLog.includes('cloud_test_cloud_ok')) {
                newClassName = 'matched-color';
            } else if (lastLog.includes('Err:') || lastLog.endsWith('error') || lastLog.endsWith('failed') || lastLog.endsWith('fail')) {
                newClassName = 'match-error-color';
            }
        }
        
        setClassNames(['mapping-log-container', newClassName]);
    }, [mappingLogsResult, openMappingLog])

    const onBlur = useCallback(() => setActiveInput(null), [setActiveInput])

    const onToggleCloudEnabled = useCallback(() => {
        const next = !cloudEnabled
        rpc.setCloudStatus(next)
            .then(() => {
                rpc.getCloudStatus().then((enabled) => {
                    if (enabled === true || enabled === false) {
                        dispatch(setCloudStatus(enabled))
                    }
                }).catch(() => {})
            })
            .catch((err) => {
                failure(err)
            })
    }, [cloudEnabled, rpc, failure, dispatch])

    const onClickSignIn = useCallback(() => {
        setIsLoading(true)
        setLoadingType('login')
        rpc.loginNovaCloud({email, password})
            .then(() => {
                success(t("login_successful"))
                setTimeout(() => {
                    window.location.reload()
                }, 1200)
            })
            .catch(failure)
            .finally(() => {
                setIsLoading(false)
                setLoadingType(null)
            })
    }, [success, failure, rpc, email, password, t])

    const onClickLogout = useCallback(() => {
        dispatch(setPopupScreenAction(
            true,
            'question',
            t("do_you_want_to_log_out"),
            null,
            (isApproved, deleteAllData) => {
                if (isApproved) {
                    setIsLoading(true)
                    setLoadingType('logout')
                    rpc.logoutNovaCloud(deleteAllData)
                        .then(() => {
                            success(deleteAllData ? t("cloud_menu.all_data_deleted_logged_out") : t("cloud_menu.logout_success"))
                            setTimeout(() => {
                                window.location.reload()
                            }, 1200)
                        })
                        .catch(failure)
                        .finally(() => {
                            setIsLoading(false)
                            setLoadingType(null)
                        })
                }
            },
            true,
            {
                text: (deleteAllData) => deleteAllData ? t("log_out") : t("log_out"),
                timed: false
            },
            false,
            true,
            t("cloud_menu.delete_warning")
        ))
    }, [dispatch, t, success, failure, rpc])

    const onClickUpdateServices = useCallback(() => {
        dispatch(setPopupScreenAction(
            true,
            'question',
            t("cloud_menu.do_you_want_to_update_connection_services"),
            null,
            (isApproved) => {
                if (isApproved) {
                    setIsLoading(true)
                    setLoadingType('serviceUpdate')
                    rpc.updateConnectionServices()
                        .then(() => {
                            success(t("cloud_menu.service_update_success"))
                        })
                        .catch((error) => {
                            failure(error)
                        })
                        .finally(() => {
                            setIsLoading(false)
                            setLoadingType(null)
                        })
                }
            },
            true,
            true
        ))
    }, [dispatch, rpc, failure, success, t])

    const onClickConnectionTest = useCallback(() => {
        const gatewayId = selectedGateway?.id
        setMappingLogsResult([])
        dispatch(setCloudConnectionStatusAction(""))
        setOpenMappingLog(true)
        
        rpc.getCloudConnectionTest(gatewayId)
            .then((result) => {
                dispatch(setCloudConnectionStatusAction(JSON.stringify(result)))
            })
            .catch((error) => {
                failure(t(error))
            })
    }, [rpc, selectedGateway, failure, t, dispatch])

    const onClickCloseMappingLog = useCallback(() => {
        setOpenMappingLog(false)
        dispatch(setCloudConnectionStatusAction(""))
        setMappingLogsResult([])
    }, [dispatch])

    const buttons = useMemo(() => [
        {
            text: t('Update'),
            className: 'green',
            hidden: !cloudConfig?.access_token,
            onClick: onClickUpdateServices,
        },
        {
            text: t('cloud_menu.test_cloud_connection'),
            className: 'blue',
            hidden: cloudConfig?.access_token,
            onClick: onClickConnectionTest,
        }
        
    ], [ t, cloudConfig, onClickUpdateServices, onClickConnectionTest]);

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings"}>
            {openMappingLog && (
        <MappingLogPopupScreen
            logs={mappingLogsResult}
            isDone={true}
            isFormatted={false}
            scrollToBottom={false}
            classNames={classNames}
            onClickClose={onClickCloseMappingLog}
                />
            )}
                <div className="content-divider">
                    <div className="content-wrapper">
                        {!cloudConfig?.access_token && (
                            <SettingCheckBoxComponent
                                label={t("active_passive")}
                                value={cloudEnabled}
                                set={onToggleCloudEnabled}
                                disabled={isLoading}
                            />
                        )}
                        {cloudEnabled && (
                            <>
                                <SettingInputComponent
                                    placeholder={t("cloud_menu.email")}
                                    label={t("cloud_menu.email")}
                                    value={email}
                                    setValue={setEmail}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                    autoComplete="off"
                                    disabled={Boolean(cloudConfig?.access_token)}
                                />
                            </>
                        )}
                        {cloudEnabled && !cloudConfig?.access_token && (
                            <SettingInputComponent
                                placeholder={t("password")}
                                label={t("password")}
                                type="password"
                                value={password}
                                setValue={setPassword}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                                autoComplete="new-password"
                            />
                        )}
                        <div className="cloud-action-container">
                            {!cloudConfig?.access_token ? (
                                cloudEnabled ? (
                                    <button {...onClickHandler(onClickSignIn)} disabled={isLoading}>
                                        {t("cloud_menu.login")}
                                    </button>
                                ) : null
                            ) : (
                                <button
                                    {...onClickHandler(onClickLogout)}
                                    style={{backgroundColor: "#b60e44"}}
                                    disabled={isLoading}
                                >
                                    {t("cloud_menu.log_outbtn")}
                                </button>
                            )}
                        </div>
                    </div>
                    {!cloudConfig?.access_token ? (
                        <div className="content-wrapper"></div>
                    ):(
                        <div className="content-wrapper connection-services">
                            <h3 className="connection-title">{t("cloud_menu.connection_services")}</h3>
                            {connectServices && connectServices.length > 0 ? (
                                <div className="connection-services-list">
                                    {connectServices.map((index) => (
                                        <div key={index} className="service-item">
                                            <span className="connect-status"></span>
                                            <span className="service-name">{`${index} ${t("cloud_menu.connection_service")}`}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="connection-not-found">{t("cloud_menu.connection_service_not_found")}</p>
                            )}
                        </div>
                    )}
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
            {isLoading && (
                <BusyIndicator 
                    message={
                        loadingType === 'login' 
                            ? t("cloud_menu.please_wait_while_services_are_loading") 
                            : loadingType === 'logout'
                                ? t("cloud_menu.please_wait_while_logging_out")
                                : t("cloud_menu.please_wait_connection_services_updated")
                    } 
                />
            )}
        </PasswordContainer>
    )
})
